import React from "react";
import { useCallback } from "react";
import { useMemo } from "react";
import { FC } from "react";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import LabeledContent from "../../../../components/LabeledContent";
import { User } from "../../types";
import Avatar from "@material-ui/core/Avatar";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import MenuIcon from "@material-ui/icons/Menu";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { Link } from "react-router-dom";
import { Routes } from "../../../../constants/routes";
import ListItemText from "@material-ui/core/ListItemText";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import useUserQueries from "../../hooks/useUserQueries";
import { mutateErrors } from "../../../../utils";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import { useWizard } from "../../../wizard/context";
import { SubmitButtonColor } from "../../../wizard/types";
import { UserRole } from "../../enums/UserRole";

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    height: "100%",
  },
  cardVerified: {
    boxShadow: `0 0 4px 0 ${theme.palette.success.dark}`,
  },
  cardNotVerified: {
    boxShadow: `0 0 4px 0 ${theme.palette.error.main}`,
  },
  cardContent: {
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  menu: {
    "& .MuiListItemIcon-root": {
      // eslint-disable-line no-useless-computed-key
      minWidth: 30,
    },
  },
  line: {
    marginBottom: theme.spacing(1),
    "&:last-child": {
      marginBottom: 0,
    },
  },
}));

export default function UserListItem({
  user,
  refetch,
}: {
  user: User;
  refetch: Function;
}) {
  const classes = useStyles();

  const roleText = useMemo(() => {
    switch (Number(user.role)) {
      case UserRole.Owner:
        return "Owner";
      case UserRole.Admin:
        return "Admin";
      case UserRole.Agent:
        return "Agent";
      case UserRole.School:
        return user.schoolProfile?.school?.name || "School";
      case UserRole.ServiceProvider:
        return "Service provider";
      case UserRole.Support:
        return "Support";
      default:
        return "Student";
    }
  }, [user]);

  const registered = useMemo(() => {
    return moment(user.created_at).format("MMM DD YYYY, h:mm:ss a");
  }, [user]);

  const verifiedProfileClass = useMemo(
    () =>
      typeof user.profile_verified == "undefined"
        ? ""
        : user.profile_verified
          ? classes.cardVerified
          : classes.cardNotVerified,
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid item xs={12} sm={4} md={3}>
      <Card className={clsx(classes.card, verifiedProfileClass)}>
        <CardHeader
          title={
            <Typography>
              {roleText}
              {user.studentProfile?.agent ? "(agent)" : ""}
            </Typography>
          }
          avatar={
            <Avatar
              src={
                user.schoolProfile?.school?.image?.preview ||
                (user.photo && user.photo.preview)
              }
            />
          }
          action={<UserMenu user={user} refetch={refetch} />}
        />
        <CardContent className={classes.cardContent}>
          <LabeledContent label={"Name"} className={classes.line}>
            {user.name}
          </LabeledContent>
          <LabeledContent label={"Email"} className={classes.line}>
            {user.verified ? (
              <CheckCircleIcon color={"action"} />
            ) : (
              <CancelIcon color={"error"} />
            )}{" "}
            {user.email}
          </LabeledContent>
          <LabeledContent label={"Registered"} className={classes.line}>
            {registered}
          </LabeledContent>
        </CardContent>
      </Card>
    </Grid>
  );
}

const useMenuStyles = makeStyles((theme: Theme) => ({
  root: {
    "& .MuiListItemIcon-root": {
      minWidth: 30,
    },
  },
}));

const UserMenu: FC<{ user: User; refetch: Function }> = React.memo(
  ({ user, refetch }) => {
    const classes = useMenuStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { openWizard } = useWizard();

    const [menuEl, setMenuEl] = React.useState(null);

    const {
      fetchVerify,
      fetchUnVerify,
      fetchVerifyProfile,
      fetchUnVerifyProfile,
      fetchDelete,
    } = useUserQueries();

    const openMenu = useCallback((e) => {
      setMenuEl(e.target);
    }, []);

    const closeMenu = useCallback((e) => {
      setMenuEl(null);
    }, []);

    const handleVerify = useCallback(
      async (event) => {
        const query = user.verified ? fetchUnVerify : fetchVerify;
        try {
          await query(user.id);
          enqueueSnackbar("Success", { variant: "success" });
          refetch();
        } catch (error) {
          const { message } = mutateErrors(error);
          enqueueSnackbar(message, { variant: "error" });
        }
        closeMenu(event);
      },
      [user, refetch, closeMenu],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDelete = useCallback(
      async (event) => {
        closeMenu(event);
        const isConfirmed = await openWizard({
          steps: [
            {
              titleConfig: {
                titleText: "Delete user",
              },
              content: `Are you sure you want to delete "${user.name}"?`,
            },
          ],
          submitButtonText: "Delete",
          submitButtonColor: SubmitButtonColor.Danger,
          cancelButtonText: "Cancel",
        });

        if (!isConfirmed) return;

        try {
          await fetchDelete(user.id);
          enqueueSnackbar("Success", { variant: "success" });
          refetch();
        } catch (error) {
          const { message } = mutateErrors(error);
          enqueueSnackbar(message, { variant: "error" });
        }
      },
      [user, refetch, closeMenu],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    const handleVerifyProfile = useCallback(
      async (event) => {
        const query = user.profile_verified
          ? fetchUnVerifyProfile
          : fetchVerifyProfile;
        try {
          await query(user.id);
          enqueueSnackbar("Success", { variant: "success" });
          refetch();
        } catch (error) {
          const { message } = mutateErrors(error);
          enqueueSnackbar(message, { variant: "error" });
        }
        closeMenu(event);
      },
      [user, refetch, closeMenu],
    ); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <IconButton onClick={openMenu}>
          <MenuIcon />
        </IconButton>
        <Menu
          disableScrollLock
          className={classes.root}
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={closeMenu}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={closeMenu}
            component={Link}
            to={`/0/${Routes.USERS}/${user.id}/edit`}
          >
            <ListItemText>Account</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleVerify}>
            {user.verified ? (
              <ListItemText>UnVerify</ListItemText>
            ) : (
              <ListItemText>Verify</ListItemText>
            )}
          </MenuItem>
          {user.hasOwnProperty("profile_verified") && (
            <MenuItem onClick={handleVerifyProfile}>
              {user.profile_verified ? (
                <ListItemText>UnApprove</ListItemText>
              ) : (
                <ListItemText>Approve</ListItemText>
              )}
            </MenuItem>
          )}
          <MenuItem onClick={handleDelete}>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  },
);
